/**
 * WhiteBIT timeframe configuration and interval mapping.
 * Supports: 1m, 3m, 5m, 15m, 30m, 1h, 2h, 4h, 6h, 8h, 12h, 1d, 3d, 1w, 1M.
 * @see https://docs.whitebit.com/public/http-v1/#kline
 * @see https://docs.whitebit.com/public/websocket/#kline
 */

import {
  registerExchangeTimeframes,
  resolutionToSeconds,
  type TimeframeConfig,
} from '../../lib/timeframes';

/**
 * WhiteBIT supported kline intervals.
 */
export const WHITEBIT_TIMEFRAMES: TimeframeConfig = {
  native: [
    '1m',
    '3m',
    '5m',
    '15m',
    '30m',
    '1h',
    '2h',
    '4h',
    '6h',
    '8h',
    '12h',
    '1d',
    '3d',
    '1w',
    '1M',
  ],
  synthetic: ['1s'], // Use ticker for 1s
  minIntervalSeconds: 60,
};

// Register with central registry
registerExchangeTimeframes('whitebit', WHITEBIT_TIMEFRAMES);

/**
 * Maps TradingView resolution to WhiteBIT WebSocket interval (in seconds).
 */
export const mapToWhitebitInterval = (resolution: string): number => {
  const seconds = resolutionToSeconds(resolution);

  // Exact matches for all supported intervals
  if (seconds === 60) return 60; // 1m
  if (seconds === 180) return 180; // 3m
  if (seconds === 300) return 300; // 5m
  if (seconds === 900) return 900; // 15m
  if (seconds === 1800) return 1800; // 30m
  if (seconds === 3600) return 3600; // 1h
  if (seconds === 7200) return 7200; // 2h
  if (seconds === 14400) return 14400; // 4h
  if (seconds === 21600) return 21600; // 6h
  if (seconds === 28800) return 28800; // 8h
  if (seconds === 43200) return 43200; // 12h
  if (seconds === 86400) return 86400; // 1d
  if (seconds === 259200) return 259200; // 3d
  if (seconds === 604800) return 604800; // 1w
  if (seconds === 2592000) return 2592000; // 1M (30 days)

  // Default to closest supported interval (round down)
  if (seconds < 60) return 60;
  if (seconds < 180) return 60;
  if (seconds < 300) return 180;
  if (seconds < 900) return 300;
  if (seconds < 1800) return 900;
  if (seconds < 3600) return 1800;
  if (seconds < 7200) return 3600;
  if (seconds < 14400) return 7200;
  if (seconds < 21600) return 14400;
  if (seconds < 28800) return 21600;
  if (seconds < 43200) return 28800;
  if (seconds < 86400) return 43200;
  if (seconds < 259200) return 86400;
  if (seconds < 604800) return 259200;
  if (seconds < 2592000) return 604800;

  return 2592000;
};

/**
 * Maps TradingView resolution to WhiteBIT REST API interval format.
 */
export const mapToWhitebitRestInterval = (resolution: string): string => {
  const seconds = resolutionToSeconds(resolution);

  // Exact matches for all supported intervals
  if (seconds === 60) return '1m';
  if (seconds === 180) return '3m';
  if (seconds === 300) return '5m';
  if (seconds === 900) return '15m';
  if (seconds === 1800) return '30m';
  if (seconds === 3600) return '1h';
  if (seconds === 7200) return '2h';
  if (seconds === 14400) return '4h';
  if (seconds === 21600) return '6h';
  if (seconds === 28800) return '8h';
  if (seconds === 43200) return '12h';
  if (seconds === 86400) return '1d';
  if (seconds === 259200) return '3d';
  if (seconds === 604800) return '1w';
  if (seconds === 2592000) return '1M';

  // Default to closest supported interval (round down)
  if (seconds < 60) return '1m';
  if (seconds < 180) return '1m';
  if (seconds < 300) return '3m';
  if (seconds < 900) return '5m';
  if (seconds < 1800) return '15m';
  if (seconds < 3600) return '30m';
  if (seconds < 7200) return '1h';
  if (seconds < 14400) return '2h';
  if (seconds < 21600) return '4h';
  if (seconds < 28800) return '6h';
  if (seconds < 43200) return '8h';
  if (seconds < 86400) return '12h';
  if (seconds < 259200) return '1d';
  if (seconds < 604800) return '3d';
  if (seconds < 2592000) return '1w';

  return '1M';
};
